import { useState } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import LegacyImage from 'next/legacy/image';
import Url from 'lib/siteurls';
import { mqLargeMin } from 'styled/tools/media-queries';
import {
  Fontw35s32h40,
  Fontw35s48h56,
  Fontw4s12h24,
  Fontw4s14h20,
  Fontw4s16h24,
} from 'styles/commonCss';
import AddCircleIcon from './AddCircleIcon';
import { BoardMember } from 'lib/contentful/practitioners/get-practitioners-contentful-data';
import cloudinaryLoader from 'lib/image/cloudinary-loader';
import { mqSmallMin, mqSmallMax } from 'styled/tools/media-queries';

const Section = styled.section`
  background: linear-gradient(
    180deg,
    rgba(249, 249, 249, 0) 0%,
    var(--fill-primary) 100%
  );
  display: flex;
  justify-content: center;
  flex-direction: column;
  padding: 48px 24px;
  gap: 24px;

  @media ${mqLargeMin} {
    padding: 48px 48px 24px 48px;
    gap: 0;
  }
`;

const Title = styled.div`
  ${Fontw35s48h56}
  letter-spacing: -0.48px;
  margin-right: auto;

  @media ${mqSmallMax} {
    ${Fontw35s32h40}
  }

  @media ${mqLargeMin} {
    padding-bottom: 96px;
  }
`;

const Content = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;

  @media ${mqLargeMin} {
    gap: 48px;
  }
`;

const Card = styled.div`
  @media ${mqSmallMin} {
    width: calc(25% - 18px);
  }

  @media ${mqLargeMin} {
    width: calc(25% - 36px);
  }

  width: calc(50% - 12px);
`;

const Media = styled.div`
  max-width: 300px;
`;

const BoardMemberInfo = styled.div`
  max-width: 300px;
  margin-top: 15px;
  ${Fontw4s16h24}
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;

  @media ${mqSmallMax} {
    ${Fontw4s12h24}
  }

  @media ${mqLargeMin} {
    margin-top: 14px;
  }
`;

const Name = styled.div`
  font: inherit;
  color: var(--text-primary);
`;

const FieldOfStudy = styled.div`
  font: inherit;
  color: #1c3a13cc;
`;

const ShowMoreWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 0;

  @media ${mqLargeMin} {
    padding-top: 24px;
  }
`;

const ShowMore = styled.button`
  ${Fontw4s14h20}
  display: flex;
  justify-content: center;
  align-items: center;
  background: inherit;
  border: none;
  cursor: pointer;
  text-decoration: underline;
`;

const Padding = styled.div`
  height: 24px;

  @media ${mqSmallMax} {
    display: none;
`;

const BoardMembers: React.FC<{ boardMembers: BoardMember[] }> = ({
  boardMembers,
}) => {
  const [showAll, setShowAll] = useState(false);

  const displayedBoardMembers = showAll
    ? boardMembers
    : boardMembers.slice(0, 4);

  return (
    <Section>
      <Title>
        Select Clinical + Scientific
        <br />
        Board Members
      </Title>
      <Content>
        {displayedBoardMembers.map((boardMember) => (
          <Card key={boardMember.name}>
            <Media>
              <LegacyImage
                src={`${Url.cloudinary}/f_auto,q_auto,c_fill,g_face,w_400,ar_4:6/${boardMember.image}`}
                width={400}
                height={600}
                alt={`${boardMember.name} ${boardMember.fieldOfStudy}`}
                loader={cloudinaryLoader}
              />
            </Media>
            <BoardMemberInfo>
              <Name>{boardMember.name}</Name>
              <FieldOfStudy>{boardMember.fieldOfStudy}</FieldOfStudy>
            </BoardMemberInfo>
          </Card>
        ))}
      </Content>
      {!showAll ? (
        <ShowMoreWrapper>
          <ShowMore type='button' onClick={() => setShowAll(true)}>
            <AddCircleIcon />
            Show More
          </ShowMore>
        </ShowMoreWrapper>
      ) : (
        <Padding />
      )}
    </Section>
  );
};

export default BoardMembers;
